import React from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Link as MuiLink,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

// Ensure external links have a protocol so they don't get treated as in-app routes
const normalizeUrl = (u) => {
  if (!u) return undefined;
  if (/^(https?:)?\/\//i.test(u)) {
    // already has http/https or protocol-relative //
    return u.startsWith("//") ? `https:${u}` : u;
  }
  return `https://${String(u).replace(/^\/*/, "")}`;
};

const ModisaneLaw = ({ businessCard, companyConfig, handleAddToContacts }) => {
  // Accent + palette (tweak per brand if needed)
  const ACCENT = companyConfig?.primaryColor || "#E9C874"; // soft gold
  const BG = companyConfig?.backgroundColor || "#0E0E0E";
  const CARD_BG = companyConfig?.paperBackground || "#151515";
  const TXT = companyConfig?.textColor || "#FFFFFF";

  const firstName = businessCard?.name?.split(" ")[0] || "Guest";

  // Safe links (prefer per-card values from the form; fall back to company defaults)
  const phoneHref = businessCard?.phone
    ? `tel:${businessCard.phone}`
    : undefined;
  const emailHref = businessCard?.email
    ? `mailto:${businessCard.email}`
    : undefined;

  // Profile can be a full URL (profileUrl), a path/slug (profileLink), or built from a base
  const profileHref =
    businessCard?.profileUrl ||
    businessCard?.profileLink ||
    (companyConfig?.profileBaseUrl &&
    (businessCard?.slug || businessCard?.profileSlug)
      ? `${companyConfig.profileBaseUrl}${
          businessCard?.slug || businessCard?.profileSlug
        }`
      : undefined);

  // Website: allow per-card overrides (website / websiteUrl), then companyConfig
  const webHref =
    businessCard?.website ||
    businessCard?.websiteUrl ||
    companyConfig?.website ||
    undefined;

  // LinkedIn: allow per-card overrides (linkedin / linkedinUrl), then companyConfig social
  const linkedinHref =
    businessCard?.linkedin ||
    businessCard?.linkedinUrl ||
    companyConfig?.socialIcons?.linkedin ||
    undefined;

  // Normalize external links to include https:// when missing
  const safeProfileHref = normalizeUrl(profileHref);
  const safeWebHref = normalizeUrl(webHref);
  const safeLinkedinHref = normalizeUrl(linkedinHref);

  // A reusable round icon button with stroke
  const RoundIcon = ({ children, href, onClick, ariaLabel }) => (
    <IconButton
      aria-label={ariaLabel}
      href={href}
      onClick={onClick}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
      sx={{
        width: 68,
        height: 68,
        borderRadius: "50%",
        border: `2px solid ${ACCENT}`,
        color: ACCENT,
        transition: "transform .18s ease, background-color .18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          backgroundColor: "rgba(233,200,116,0.08)",
        },
      }}
    >
      {children}
    </IconButton>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: BG,
        color: TXT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        fontFamily: companyConfig?.fontFamily || "'Montserrat', sans-serif",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 360,
          backgroundColor: CARD_BG,
          borderRadius: 18,
          p: 3,
          boxShadow: "0 10px 30px rgba(0,0,0,.35)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Logo */}
        {companyConfig?.logo && (
          <Box sx={{ width: "70%", maxWidth: 220, mt: 0.5 }}>
            <img
              src={companyConfig.logo}
              alt={`${companyConfig.name} Logo`}
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </Box>
        )}

        {/* Avatar */}
        <Avatar
          alt={businessCard?.name}
          src={businessCard?.profilePicURL}
          sx={{
            width: 108,
            height: 108,
            border: `3px solid ${ACCENT}`,
            boxShadow: "0 6px 16px rgba(0,0,0,.35)",
          }}
        />

        {/* Name & Title */}
        <Box sx={{ textAlign: "center", mt: 0.5 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.15rem",
              letterSpacing: ".04em",
              color: ACCENT,
              lineHeight: 1.2,
            }}
          >
            {businessCard?.name || "Full Name"}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: ".78rem",
              letterSpacing: ".18em",
              color: "rgba(255,255,255,.75)",
              textTransform: "uppercase",
            }}
          >
            {businessCard?.position || "Job Title"}
          </Typography>
        </Box>

        {/* 3×2 Grid of round icon actions */}
        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2.5,
            width: "100%",
            justifyItems: "center",
          }}
        >
          {/* Row 1: Phone / Email / Profile */}
          <RoundIcon ariaLabel="Call" href={phoneHref}>
            <PhoneIcon />
          </RoundIcon>

          <RoundIcon ariaLabel="Email" href={emailHref}>
            <EmailIcon />
          </RoundIcon>

          <RoundIcon ariaLabel="Profile" href={safeProfileHref}>
            <PersonOutlineIcon />
          </RoundIcon>

          {/* Row 2: Website / LinkedIn / Add Contact */}
          <RoundIcon ariaLabel="Website" href={safeWebHref}>
            <LanguageIcon />
          </RoundIcon>

          <RoundIcon ariaLabel="LinkedIn" href={safeLinkedinHref}>
            <LinkedInIcon />
          </RoundIcon>

          <RoundIcon ariaLabel="Add to contacts" onClick={handleAddToContacts}>
            <AddCircleOutlineIcon />
          </RoundIcon>
        </Box>
      </Box>
    </Box>
  );
};

export default ModisaneLaw;
